import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { initialPageSize } from "./preferredPageSize";

const DEFAULT_LIMIT = 15;
const DEFAULT_DEBOUNCE_MS = 800;

const useSearchState = (
	searchTerm: string | undefined,
	debounceMs: number,
	rememberPageSize: boolean
) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [altered, setAltered] = useState<boolean>(false);

	const paramPageValue = searchParams.get("page") || 1;
	const paramLimitValue = searchParams.get("limit");

	const [searchValue, setSearchValue] = useState<string>(searchTerm || "");
	const [query, setQuery] = useState<string>(searchTerm || "");
	const [page, setPage] = useState<number>(Number(paramPageValue));
	const [limit, setLimit] = useState<number>(() => {
		if (rememberPageSize) {
			return initialPageSize({
				urlLimit: paramLimitValue,
				urlPage: searchParams.get("page"),
				fallback: DEFAULT_LIMIT,
			});
		}

		return paramLimitValue ? Number(paramLimitValue) : DEFAULT_LIMIT;
	});
	const [isTyping, setIsTyping] = useState<boolean>(false);

	const handleSetPage = (pageParam: number) => {
		if (+pageParam !== page) {
			setPage(pageParam);
			if (!searchParams.has("page")) {
				searchParams.append("page", pageParam.toString());
			} else {
				searchParams.set("page", pageParam.toString());
			}

			// A remembered size is not in the URL until the user picks one; write it with the page,
			// or a shared link to page 3 opens at the recipient's size and shows other rows.
			if (rememberPageSize && !searchParams.has("limit")) {
				searchParams.set("limit", limit.toString());
			}

			setSearchParams(searchParams, { replace: true });
		}
	};

	const handleSetLimit = (limitParam: number) => {
		if (limitParam !== limit) {
			setLimit(limitParam);
			if (!searchParams.has("limit")) {
				searchParams.append("limit", limitParam.toString());
			} else {
				searchParams.set("limit", limitParam.toString());
			}

			setSearchParams(searchParams, { replace: true });
		}
	};

	const searchTimer = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		setIsTyping(true);
		setAltered(true);
		if (searchTimer.current) {
			clearTimeout(searchTimer.current);
		}

		searchTimer.current = setTimeout(() => {
			setQuery(searchValue);
			setIsTyping(false);
		}, debounceMs);

		// Cancel on unmount — without this the pending timer outlives the component
		// and calls setQuery/setIsTyping on an unmounted hook.
		return () => {
			if (searchTimer.current) {
				clearTimeout(searchTimer.current);
			}
		};
	}, [searchValue, debounceMs]);

	return {
		searchValue,
		setSearchValue,
		query,
		page,
		setPage: handleSetPage,
		limit,
		setLimit: handleSetLimit,
		searchParams,
		setSearchParams,
		isTyping,
		altered,
		setAltered,
		setQuery,
	};
};

/** Search, page and page size kept in the URL; the page size starts at the app's default of 15. */
export const useSearch = (searchTerm?: string, debounceMs: number = DEFAULT_DEBOUNCE_MS) =>
	useSearchState(searchTerm, debounceMs, false);

/**
 * `useSearch` for a table with a page-size selector: its page size starts at the size the user last
 * picked in any table, unless the URL names one (PREC-4403). A list that only scrolls must keep
 * `useSearch`, or a pick on a table would enlarge its requests.
 */
export const useTableSearch = (searchTerm?: string, debounceMs: number = DEFAULT_DEBOUNCE_MS) =>
	useSearchState(searchTerm, debounceMs, true);
